/**
 * Adapter Tela 5 Painel de SLA — wire (GET /v1/sla/dashboard*, /definitions)
 * → view-models. Funções puras (espelha tela4-adapter).
 *
 * Achados R15 materializados:
 *   - `cumprimento` é fração 0-1 (card + heatmap) → Math.round(*100).
 *   - heatmap cache é por-hora (0-23); mockup usa 6 buckets de 4h → agrega.
 *   - ranking = operadores com mais violações (não compliance %); merge 30d+7d.
 * Degradação honesta (padrão Item 317): slaType desconhecido humaniza o token;
 * threshold ausente → '—' (D2).
 */
import { relativeTime } from '../../../conversations/format';

import type {
  SlaBreachRaw,
  SlaDashboardCardRaw,
  SlaHeatmapCellRaw,
  SlaRankingEntryRaw,
  SlaTemporalPatternRaw,
} from '../../types';
import type {
  SlaDrillRowVM,
  SlaHeatmapVM,
  SlaPatternVM,
  SlaRankingRowVM,
  SlaRowStatus,
  SlaRowVM,
} from '../../view-models';

const PLACEHOLDER = '—';

/** 8 slaTypes canônicos (sla-canonical.ts) — PT + ícone. */
export const SLA_TYPE_META: Record<string, { label: string; icon: string }> = {
  customer_contact_pendency: {
    label: 'Contato com cliente (pendência)',
    icon: '📞',
  },
  awaiting_operator_response: {
    label: 'Aguardando resposta do operador',
    icon: '⏰',
  },
  appointment_confirmation_d1: {
    label: 'Confirmação de agendamento D-1',
    icon: '📅',
  },
  commitment_broken_followup: {
    label: 'Follow-up de compromisso quebrado',
    icon: '💰',
  },
  conflict_alert_critical: { label: 'Alerta de conflito crítico', icon: '🚨' },
  conflict_alert_high: { label: 'Alerta de conflito alto', icon: '🔴' },
  conflict_alert_medium: { label: 'Alerta de conflito médio', icon: '🟡' },
  p1_elevation_acknowledgment: {
    label: 'Reconhecimento de elevação P1',
    icon: '🆘',
  },
};

/** Label + ícone de um slaType; humaniza token desconhecido (nunca vazio). */
export function humanizeSlaType(t: string): { label: string; icon: string } {
  return (
    SLA_TYPE_META[t] ?? {
      label: t.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
      icon: '📊',
    }
  );
}

/** Humaniza thresholdMinutes (5→'5min', 120→'2h', 1440→'24h', 90→'1h 30min'). */
export function humanizeThreshold(min: number): string {
  if (min < 60) return `${min}min`;
  if (min % 60 === 0) return `${min / 60}h`;
  return `${Math.floor(min / 60)}h ${min % 60}min`;
}

/** Bandas D3 (cumprimento já em 0-100): ≥95 ok · ≥80 warn · <80 crit. */
export function slaBand(pct: number): SlaRowStatus {
  return pct >= 95 ? 'ok' : pct >= 80 ? 'warn' : 'crit';
}

/** Borda da banda "ok" (D3) — linha de threshold renderizada na barra. */
export const SLA_THRESHOLD_LINE = 95;

export function adaptCard(
  card: SlaDashboardCardRaw,
  thresholdByDefId: Map<string, number>,
): SlaRowVM {
  const pct = Math.round(card.cumprimento * 100);
  const { label, icon } = humanizeSlaType(card.slaType);
  const min = thresholdByDefId.get(card.slaDefinitionId);
  const slaLabel = min !== undefined ? humanizeThreshold(min) : PLACEHOLDER;

  return {
    id: card.slaDefinitionId,
    slaType: card.slaType,
    label,
    icon,
    slaLabel,
    cumprimento: pct,
    threshold: SLA_THRESHOLD_LINE,
    total: card.totalDemands,
    withinSla: card.totalDemands - card.violations,
    violations: card.violations,
    status: slaBand(pct),
  };
}

const HEATMAP_BUCKETS = ['00-04', '04-08', '08-12', '12-16', '16-20', '20-24'];
// Ordem de exibição Seg→Dom mapeada de dayOfWeek (0=Dom..6=Sáb).
const HEATMAP_DAYS: { label: string; dayOfWeek: number }[] = [
  { label: 'Seg', dayOfWeek: 1 },
  { label: 'Ter', dayOfWeek: 2 },
  { label: 'Qua', dayOfWeek: 3 },
  { label: 'Qui', dayOfWeek: 4 },
  { label: 'Sex', dayOfWeek: 5 },
  { label: 'Sáb', dayOfWeek: 6 },
  { label: 'Dom', dayOfWeek: 0 },
];

export function adaptHeatmap(cells: SlaHeatmapCellRaw[]): SlaHeatmapVM {
  const rows = HEATMAP_DAYS.map(({ label, dayOfWeek }) => {
    const cellsRow = HEATMAP_BUCKETS.map((_, bucketIdx): SlaRowStatus | null => {
      const inCell = cells.filter(
        (c) => Math.floor(c.hourPeriod / 4) === bucketIdx && c.dayOfWeek === dayOfWeek,
      );
      const totalCount = inCell.reduce((acc, c) => acc + c.totalCount, 0);
      if (totalCount === 0) return null;
      const weighted =
        inCell.reduce((acc, c) => acc + c.cumprimento * c.totalCount, 0) / totalCount;
      return slaBand(Math.round(weighted * 100));
    });
    return { day: label, cells: cellsRow };
  });
  return { buckets: HEATMAP_BUCKETS, rows };
}

export function adaptRanking(
  thirtyD: SlaRankingEntryRaw[],
  sevenD: SlaRankingEntryRaw[],
  nameFor: (userId: string) => string | undefined,
): SlaRankingRowVM[] {
  const by30 = new Map(thirtyD.map((e) => [e.userId, e]));
  const by7 = new Map(sevenD.map((e) => [e.userId, e]));
  const userIds = new Set<string>([...by30.keys(), ...by7.keys()]);

  return Array.from(userIds)
    .map((userId): Omit<SlaRankingRowVM, 'rank'> => {
      const e30 = by30.get(userId);
      const e7 = by7.get(userId);
      const monthly30d = e30?.violationCount ?? 0;
      const weekly7d = e7?.violationCount ?? 0;
      const trend = e30?.trend ?? e7?.trend ?? 'stable';
      return {
        userId,
        // Nome do Engine (ranking) > presence > placeholder honesto (nunca id cru).
        name: e30?.userName ?? e7?.userName ?? nameFor(userId) ?? PLACEHOLDER,
        monthly30d,
        weekly7d,
        trend,
        atRisk: monthly30d > 0,
      };
    })
    .sort((a, b) => b.monthly30d - a.monthly30d)
    .map((row, idx) => ({ ...row, rank: idx + 1 }));
}

// ─── T5-2 drill-down + padrões ───────────────────────────────────────────────

/** Tipo de recurso (resourceType) → PT. Fallback humaniza token desconhecido. */
export const RESOURCE_TYPE_LABELS: Record<string, string> = {
  pendency: 'Pendência',
  conversation: 'Conversa',
  conflict_alert: 'Alerta de conflito',
  appointment: 'Agendamento',
  commitment: 'Compromisso',
};

function humanizeToken(t: string): string {
  return t.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

function severityLabel(s: 'warning' | 'critical'): string {
  return s === 'critical' ? 'Crítico' : 'Atenção';
}

/** Violação real → linha do drill-down. opName degrada honesto ('—'); fmtAgo sem "há". */
export function adaptBreach(
  b: SlaBreachRaw,
  nameFor: (userId: string) => string | undefined,
): SlaDrillRowVM {
  const opName =
    b.assignedToUserName ??
    (b.assignedToUserId !== null ? nameFor(b.assignedToUserId) : undefined) ??
    PLACEHOLDER;
  return {
    id: b.id,
    resourceType: b.resourceType,
    resourceLabel: RESOURCE_TYPE_LABELS[b.resourceType] ?? humanizeToken(b.resourceType),
    severity: b.severity,
    severityLabel: severityLabel(b.severity),
    opName,
    occurredAtLabel: relativeTime(b.occurredAt).replace(/^há /, ''),
    // 0min sobre o limiar = violação no limite — "0min acima" confunde.
    durationLabel:
      b.durationOverThresholdMinutes === 0
        ? 'no limite'
        : `${humanizeThreshold(b.durationOverThresholdMinutes)} acima`,
    resolved: b.resolvedAt !== null,
  };
}

/** Tipos de padrão conhecidos → título PT. Fallback humaniza o token. */
const PATTERN_TYPE_TITLES: Record<string, string> = {
  consistent_weekday_hour_breach: 'Furo recorrente em janela fixa',
};

// dayOfWeek 0=Dom..6=Sáb — nome PT plural ("Terças", "Domingos").
const DAY_NAMES_PLURAL = [
  'Domingos',
  'Segundas',
  'Terças',
  'Quartas',
  'Quintas',
  'Sextas',
  'Sábados',
];

/** Padrão temporal real → VM. day/hour null → texto genérico honesto. */
export function adaptPattern(p: SlaTemporalPatternRaw): SlaPatternVM {
  const title = PATTERN_TYPE_TITLES[p.patternType] ?? humanizeToken(p.patternType);
  const subtitle =
    p.dayOfWeek !== null && p.hourPeriod !== null
      ? `${DAY_NAMES_PLURAL[p.dayOfWeek] ?? humanizeToken(`dia ${p.dayOfWeek.toString()}`)} por volta das ${p.hourPeriod.toString()}h`
      : 'Padrão recorrente detectado';
  return { id: p.id, title, subtitle, severity: p.severity };
}
